import {
  AutocompleteController,
  DateTimePickerController,
  GroupCodesController,
  TextFieldController,
} from '@/components/controls';
import { useLocationsAsOptions, usePeople } from '@/hooks';
import { isNominalId, isOccurrenceNumber } from '@/utils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';

export function PrisonerEditorDialog({
  prisoner,
  isOpen,
  onClose,
  onDelete,
  // groupCodes,
  isDeleting,
}) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: prisoner,
  });
  const {
    query: { data: people },
  } = usePeople();
  const personOptions =
    people
      ?.filter((person) => person.collarNumber)
      .map((person) => ({
        label: person.collarNumber,
        value: person.code,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];
  const {
    query: { data: custodyUnits },
  } = useLocationsAsOptions({
    type: 'Police Station',
    subtype: 'Custody Suite',
  });
  const {
    query: { data: hospitals },
  } = useLocationsAsOptions({ type: 'Hospital' });

  useEffect(() => {
    reset(prisoner);
  }, [reset, prisoner, isOpen]);

  function handleClose() {
    onClose();
  }

  return (
    <form id="prisoner-editor-form" onSubmit={handleSubmit(onClose)}>
      <Dialog open={isOpen && !!prisoner} onClose={handleClose}>
        <DialogTitle>{`${
          prisoner.code ? 'Edit' : 'Add'
        } Person Watch/Guard`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText gutterBottom>
              Please fill in the form below to add an person watch/guard. If
              groups are not specified then the prisoner will be visible to all.
            </DialogContentText>
            <GroupCodesController
              label="Groups"
              control={control}
              name="groupCodes"
              types={['SECTOR', 'TEAM']}
            />
            <TextFieldController
              name="nominalId"
              label="Nominal ID"
              control={control}
              rules={{
                required: 'Required',
                validate: async (value) => !value || (await isNominalId(value)),
              }}
            />
            <TextFieldController
              name="occurrenceNumber"
              label="Occurrence Number"
              control={control}
              rules={{
                validate: async (value) =>
                  !value || (await isOccurrenceNumber(value)),
              }}
            />
            <TextFieldController name="name" label="Name" control={control} />
            <TextFieldController
              name="offence"
              label="Offence"
              control={control}
            />
            <AutocompleteController
              name="arrestingOfficerCode"
              label="Arresting Officer"
              control={control}
              single
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={personOptions}
            />
            <AutocompleteController
              name="investigatingOfficerCode"
              label="Investigating Officer"
              control={control}
              single
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={personOptions}
            />
            <AutocompleteController
              name="officerInCaseCode"
              label="Officer in Case"
              control={control}
              single
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={personOptions}
            />
            <AutocompleteController
              name="custodyUnitId"
              label="Custody Unit"
              control={control}
              single
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={custodyUnits.concat({ label: 'Other', value: 'Other' })}
            />
            <AutocompleteController
              name="hospitalCode"
              label="Hospital"
              control={control}
              single
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              options={hospitals.concat({ label: 'Other', value: 'Other' })}
            />
            <DateTimePickerController
              name="arrivalTime"
              label="Arrival Time"
              control={control}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {!!prisoner.code && (
            <Fragment>
              <Button color="error" onClick={onDelete} disabled={isDeleting}>
                Delete
              </Button>
              <Box sx={{ flexGrow: 1 }} />
            </Fragment>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            form="prisoner-editor-form"
            type="submit"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}
